import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAuth, selectPersist, setPersist } from '../../../../features/auth/authSlice';
import { useLoginMutation } from '../../../../features/auth/authApiSlice';
import { ReactComponent as MySpinner } from '../../../.././animations/mySpinner.svg';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import jwtDecode from 'jwt-decode';

const LoginForm = ({ onClose, fromLocationData, from, loading, setLoading, setPhone, phone, setIsErrorOpen, isErrorOpen, errorText, setErrorText }) => {
    const { t } = useTranslation();

    const [password, setPassword] = useState('');
    const [isPassword, setIsPassword] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [login] = useLoginMutation();
    const persist = useSelector(selectPersist);
    const [isPersist, setIsPersist] = useState(persist);//??

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const onSubmit = async e => {
        e.preventDefault();
        setIsSubmitting(true);
        setIsErrorOpen(false);

        try {
            const userData = await login({ identifier: phone, password }).unwrap();
            if (!userData) throw new Error('IncorrectPassword');

            dispatch(setAuth({ ...userData }));

            if (from) {
                navigate(from, { replace: true, state: { ...fromLocationData } },);
            } else {
                const role = jwtDecode(userData.accessToken)?.userInfo?.role;

                if (role === process.env.REACT_APP_ADMIN_ROLE) {
                    navigate('/admin-panel/statistics', { replace: true, state: { name: 'Howdy', page: 'Statistics' } });
                } else if (role === process.env.REACT_APP_MASTER_ROLE) {
                    navigate('/master-office', { replace: true, state: { name: 'Howdy', span: 'Greetings' } });
                } else {
                    navigate('/client-office', { replace: true, state: { name: 'Howdy', span: 'Greetings' } });
                }
            }
            onClose();

        } catch (error) {
            const status = error?.status ?? error?.originalStatus;
            const serverMessage = error?.data?.message;
            if (status === 404 || serverMessage === 'PhoneNotRegistered' || serverMessage === 'AccountNotRegistered') {
                setErrorText('AccountNotRegistered');
            } else if (status === 401 || serverMessage === 'IncorrectPassword') {
                setErrorText('IncorrectPassword');
            } else if (status === 400 || serverMessage === 'InvalidPhoneNumberFormat') {
                setErrorText('InvalidPhoneNumberFormat');
            } else {
                setErrorText('LoginUnavailable');
            }
            setIsErrorOpen(true);
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onPersistChange = () => {
        setIsPersist(!isPersist);
        dispatch(setPersist(!isPersist));
    }

    useEffect(() => {
        isPersist && localStorage.setItem('persist', isPersist);
    }, [isPersist]);

    const onPhoneInputChange = val => val.trim();

    const onClickForgotPassword = async () => {
        try {
            setLoading(true);

            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(phone)) throw new Error(t('PasswordResetEmailRequired'));
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/reset/password/request`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ identifier: phone, language: window.localStorage.getItem('i18nextLng') || 'en' }) });
            setErrorText('PasswordResetInstructionsSent');
            setIsErrorOpen(true);
            setLoading(false);
        } catch (error) {
            setErrorText(error.message);
            setIsErrorOpen(true);
            setLoading(false);
            console.error(error);
        }
    };

    return <>
        <div className="welcome-text">
            <h3>{`${t('Greetings')}`}</h3>
            <span>{t('DontHaveAnAccount')} <a data-signin="1" style={{ color: '#2a41e8', cursor: 'pointer' }} className="register-tab">{t('SignUp')}</a></span>
        </div>

        <form onSubmit={onSubmit} id="login-form">
            <div className="input-with-icon-left">
                <i className="icon-feather-user"></i>
                <input type="text"
                    value={phone}
                    onChange={e => setPhone(onPhoneInputChange(e.target.value))}
                    className="input-text with-border" name="login" id="phone" placeholder={t('PhoneOrEmail')} required />
            </div>

            <div className="input-with-eye-icon">
                <div className='input-with-icon-left'>
                    <i className="icon-material-outline-lock"></i>
                    <input type={isPassword ? 'password' : 'text'} title='Should be at least 8 characters long'
                        value={password}
                        onChange={e => {
                            setPassword(e.target.value);
                            if (isErrorOpen) setIsErrorOpen(false);
                        }}
                        className="input-text with-border" name="password" id="password" placeholder={t('Password')} required />
                </div>
                <i className={`icon-feather-eye${isPassword ? '' : '-off'}`} onClick={() => setIsPassword(!isPassword)}></i>
            </div>
        </form>

        <div className="checkbox">
            <input type="checkbox" id="persistCheckbox"
                checked={isPersist} onChange={onPersistChange} />
            <label htmlFor="persistCheckbox"><span className="checkbox-icon"></span> {t('TrustThisDevice')} </label>
        </div><br />

        <a onClick={onClickForgotPassword} style={{ color: '#2a41e8', cursor: 'pointer' }} className="forgot-password">{t('ForgotPassword')}</a><br />

        {isErrorOpen &&
            <div className="notification error" role="alert" style={{ marginTop: '12px', marginBottom: '12px' }}>
                <p>{t(errorText)}</p>
                {(errorText === 'PhoneNotRegistered' || errorText === 'AccountNotRegistered') && <a data-signin="1" className="register-tab" style={{ color: '#2a41e8', cursor: 'pointer', fontWeight: 600 }}>{t('RegisterThisNumber')}</a>}
            </div>}

        {(loading || isSubmitting)
            ? <div>
                <span className='button full-width button-sliding-icon ripple-effect'>
                    <MySpinner style={{
                        display: 'inline-block',
                        marginBottom: '-10px',
                    }} />
                    {isSubmitting ? `${t('LogIn')}...` : t('Sending')}
                </span>
            </div>
            : <button className="button full-width button-sliding-icon ripple-effect" type="submit" form="login-form">
                {t("LogIn")}
                <i className="icon-material-outline-arrow-right-alt"></i>
            </button>
        }
    </>;
};

export default LoginForm;
